import React, {} from 'react';
import { DispositifBadge, codeDispositif } from '../components/DispositifBadge';
import { SoldeDuPayable, useConfirmationSolde } from '../components/SoldeDuPayable';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LogOut, FileText, AlertCircle, CreditCard, BookOpen, DollarSign, Shield} from 'lucide-react';
import PremiumHeader from '../components/brand/PremiumHeader';
import OscopLogo from '../components/brand/OscopLogo';
import AnnouncementsBanner from '../components/AnnouncementsBanner';

import MemberProfileEdit from '../components/ProfileEdit/MemberProfileEdit';
import ProcesVerbaux from './ProcesVerbaux';
import BibliothequeDocuments from './BibliothequeDocuments';
import BackButton from '../components/BackButton';

import MessagerieDashboard from '../components/Messagerie/MessagerieDashboard';
import DocumentsGED from '../components/GED/DocumentsGED';
import VideoManager from '../components/GED/VideoManager';
import ZipSharesDashboard from '../components/GED/ZipSharesDashboard';
import SouscriptionsAvecBrouillons from '../components/SouscriptionsAvecBrouillons';
import MesFormations from '../components/MesFormations';
import MonUsageCreditsWidget from '../components/MonUsageCreditsWidget';
import MonEcheancierSEPA from '../components/MonEcheancierSEPA';
import MesMandatsSEPA from '../components/sepa/MesMandatsSEPA';
import MesMaitresOuvrage from '../components/member/MesMaitresOuvrage';
import { MesContributionsRcds } from '../components/MesContributionsRcds';
import { MesPiecesComptables } from '../components/espaceMembre/MesPiecesComptables';
import MemberSubscriptionsNew from '../components/MemberSubscriptionsNew';
import DeclarationDommageForm from '../components/DeclarationDommageForm';
import { DashboardSidebar, DashboardContent } from '../components/ui/dashboard-sidebar';

import HelpCenterV2 from '../components/HelpCenterV2';
import WelcomeTour from '../components/WelcomeTour';
import LanguageSelector from '../components/LanguageSelector';
import CreditBanner from '../components/CreditBanner';
import MesServicesLegalcoop from '../components/MesServicesLegalcoop';
import MesContratsCcmi from '../components/MesContratsCcmi';
import MesDocumentsMembre from '../components/MesDocumentsMembre';
import NotificationBell from '../components/NotificationBell';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import useMemberDashboard from './useMemberDashboard';

export default function MemberDashboard() {
  const ctx = useMemberDashboard();
  const {
    _handleDownloadAttestation, _handleDownloadConditionsGenerales, _handleDownloadConditionsParticulieres, _handleDownloadProposition, _handleDownloadRecuESS, _handleSignerConditionsGenerales,
    _handleSignerConditionsParticulieres, _handleSignerProposition, _handleTrackedClick, _handleViewDocument, activeTab, dommages,
    fetchMemberData, getAuthHeader, handleLogout, handleTabChange, loading, logout,
    member, navigate, searchParams, setActiveTab, setDommages, setLoading,
    setMember, setSearchParams, setShowDeclarationForm, setSouscriptions, showDeclarationForm, sidebarTabs,
    souscriptions, t, tabFromUrl, trackClick, user, visibleSidebarTabs} = ctx;
  useConfirmationSolde(getAuthHeader, fetchMemberData);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coppam-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Bandeau Crédits ESS */}
      <CreditBanner />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-[52px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <BackButton />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-coppam-blue">Espace Membre</h1>
                  {user?.plan_actif_nom && (
                    <Badge className="bg-purple-600 text-white border-0 px-3 py-1 font-semibold">
                      {user.plan_actif_nom}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-600">Numéro de membre: <span className="font-semibold text-coppam-gold">{member?.numero_membre}</span></p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <LanguageSelector variant="compact" />
              <Button onClick={handleLogout} variant="outline" data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bandeau Premium O'SCOP — Bienvenue Membre */}
        <PremiumHeader
          eyebrow="Espace Membre — Bénéficiaire ESS"
          title={`Bienvenue ${member?.prenom || user?.prenom || ''} ${member?.nom || user?.nom || ''}`}
          subtitle="Retrouvez ici vos documents, vos demandes, l'historique de vos interventions et toutes les informations liées à votre accompagnement solidaire."
          testId="member-welcome-premium"
          className="mb-6"
        />

        {/* Annonces & Communications (visibles par les Membres) */}
        <AnnouncementsBanner space="member" />

        {/* Carte mission / accompagnement */}
        <div className="relative bg-white border border-slate-200/80 rounded-2xl p-6 mb-8 ring-1 ring-[#DEA900]/15">
          <span className="absolute left-0 top-6 bottom-6 w-1 rounded-r-full bg-[#DEA900]" />
          <div className="pl-3 text-slate-700 space-y-2 text-sm leading-relaxed">
            <p>
              Avec la <strong className="text-[#0a3d62]">messagerie dédiée</strong>, restez en contact avec votre Conseiller ESS.
            </p>
            <p className="text-[#0a3d62] font-semibold italic pt-2 border-t border-slate-100 mt-2">
              Nous sommes à vos côtés dans un esprit de coopération et d'entraide.
            </p>
          </div>
        </div>

        {/* Stats Cards — Premium navy/or/emerald */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="group relative bg-white border border-slate-200/80 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(10,61,98,0.18)]">
            <span className="absolute left-0 top-5 bottom-5 w-1 rounded-r-full bg-[#0a3d62]" />
            <div className="flex items-center justify-between pl-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Souscriptions actives</p>
                <p className="text-3xl font-bold tracking-tight tabular-nums mt-1 text-[#0a3d62]">{souscriptions.filter(s => s.is_active).length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-[#0a3d62]/8">
                <FileText className="h-6 w-6 text-[#0a3d62]" strokeWidth={2.2} />
              </div>
            </div>
          </div>

          <div className="group relative bg-white border border-slate-200/80 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(222,169,0,0.18)]">
            <span className="absolute left-0 top-5 bottom-5 w-1 rounded-r-full bg-[#DEA900]" />
            <div className="flex items-center justify-between pl-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Dommages</p>
                <p className="text-3xl font-bold tracking-tight tabular-nums mt-1 text-[#b88600]">{dommages.length}</p>
              </div>
              <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-[#DEA900]/12">
                <AlertCircle className="h-6 w-6 text-[#b88600]" strokeWidth={2.2} />
              </div>
            </div>
          </div>

          <div className="group relative bg-white border border-slate-200/80 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(16,185,129,0.18)]">
            <span className={`absolute left-0 top-5 bottom-5 w-1 rounded-r-full ${member?.adhesion_ftpe ? 'bg-emerald-500' : 'bg-rose-500'}`} />
            <div className="flex items-center justify-between pl-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Adhésion FTPE</p>
                <div className="mt-2">
                  {member?.adhesion_ftpe ? (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white font-semibold">Active</Badge>
                  ) : (
                    <Badge className="bg-rose-600 hover:bg-rose-600 text-white font-semibold">Inactive</Badge>
                  )}
                </div>
              </div>
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${member?.adhesion_ftpe ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                <CreditCard className={`h-6 w-6 ${member?.adhesion_ftpe ? 'text-emerald-600' : 'text-rose-500'}`} strokeWidth={2.2} />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className="flex gap-4 md:gap-6">
          <DashboardSidebar 
            tabs={visibleSidebarTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          
          <DashboardContent className="space-y-4">
            {activeTab === 'souscriptions' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2">
                    <SouscriptionsAvecBrouillons
                      souscriptions={souscriptions}
                      onRefresh={fetchMemberData}
                      getAuthHeader={getAuthHeader}
                    />
                  </div>
                  <div className="lg:col-span-1 space-y-4">
                    <MonUsageCreditsWidget getAuthHeader={getAuthHeader} />
                    <MesContributionsRcds getAuthHeader={getAuthHeader} />
                    <MonEcheancierSEPA getAuthHeader={getAuthHeader} />
                    <MesMandatsSEPA getAuthHeader={getAuthHeader} />
                    <MesContratsCcmi />
                    <MesServicesLegalcoop />
                  </div>
                </div>
                <MesMaitresOuvrage getAuthHeader={getAuthHeader} />
              </div>
            )}

            {activeTab === 'souscriptions-OLD' && (
              <div className="space-y-6">
                {/* Statistiques */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-600">Dispositifs Actifs</p>
                          <p className="text-3xl font-bold text-green-600">
                            {souscriptions.filter(s => s.is_active).length}
                          </p>
                        </div>
                        <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                          <FileText className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-600">Total Garanties</p>
                          <p className="text-3xl font-bold text-blue-600">
                            {souscriptions.length}
                          </p>
                        </div>
                        <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <FileText className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-orange-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-600">Montant Total</p>
                          <p className="text-3xl font-bold text-orange-600">
                            {souscriptions.reduce((acc, s) => acc + (s.montant_total || 0), 0)}€
                          </p>
                        </div>
                        <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                          <DollarSign className="h-6 w-6 text-orange-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Liste des dispositifs — header premium navy/or */}
                <Card className="border border-slate-200/80">
                  <CardHeader className="group/header relative overflow-hidden bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] text-white p-5">
                    <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")" }} />
                    <div className="relative flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-[#DEA900] flex items-center justify-center shadow-md shadow-[#DEA900]/30 shrink-0">
                        <OscopLogo size={28} variant="on-navy" />
                      </div>
                      <div>
                        <CardTitle className="text-white text-lg leading-none">Mes Dispositifs Souscrits</CardTitle>
                        <CardDescription className="text-[#f5cc3e] text-[11px] uppercase tracking-wider mt-1.5">
                          Liste de vos garanties solidaires actives
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {souscriptions.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                        <p className="text-slate-600 font-medium">Aucun dispositif souscrit</p>
                        <p className="text-sm text-slate-500 mt-2">
                          Vos souscriptions apparaîtront ici
                        </p>
                        <Button className="mt-4" onClick={() => navigate('/souscription')}>
                          <FileText className="h-4 w-4 mr-2" />
                          Souscrire à un dispositif
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {souscriptions.map((sub) => (
                          <Card key={sub.id} className={`border-2 transition-all hover:shadow-lg ${
                            sub.is_active 
                              ? 'border-green-200 bg-green-50/30' 
                              : 'border-slate-200 bg-slate-50/30'
                          }`}>
                            <CardContent className="pt-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <FileText className="h-5 w-5 text-coppam-blue" />
                                    <h3 className="font-bold text-lg text-slate-900">
                                      {sub.dispositif?.nom || 'Dispositif'}
                                    </h3>
                                    <DispositifBadge code={codeDispositif(sub)} testId={`member-dispositif-badge-${sub.id}`} />
                                  </div>
                                  {sub.solde_du && <div className="my-2"><SoldeDuPayable solde={sub.solde_du} getAuthHeader={getAuthHeader} /></div>}
                                  <p className="text-sm text-slate-600 font-mono">
                                    Réfs. {sub.numero_police}
                                  </p>
                                </div>
                                {sub.is_active ? (
                                  <Badge className="bg-green-700 text-white font-semibold">
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge className="bg-slate-700 text-white font-semibold">
                                    Expirée
                                  </Badge>
                                )}
                              </div>

                              <div className="space-y-3 bg-white rounded-lg p-3 mb-4">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-slate-600">Montant</span>
                                  <span className="font-bold text-coppam-blue">{sub.montant_total}€</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-slate-600">Valide jusqu'au</span>
                                  <span className="font-bold text-slate-900">
                                    {new Date(sub.date_fin).toLocaleDateString('fr-FR')}
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2">
                                <Button variant="outline" size="sm" className="w-full">
                                  <FileText className="h-4 w-4 mr-2" />
                                  Voir Détails
                                </Button>
                                <Button variant="outline" size="sm" className="w-full">
                                  <CreditCard className="h-4 w-4 mr-2" />
                                  Documents
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                )}
              </CardContent>
            </Card>
              </div>
            )}

            {activeTab === 'mes-documents' && (
              <div className="space-y-4">
                <MesPiecesComptables getAuthHeader={getAuthHeader} />
                <MesDocumentsMembre membreId={user?.id || user?.membre_id} />
              </div>
            )}

            {/* Onglet Documents */}
            {activeTab === 'documents' && (
              <DocumentsGED getAuthHeader={getAuthHeader} />
            )}

            {/* Onglet Photos/Vidéos */}
            {activeTab === 'videos' && (
              <VideoManager userRole="member" showUpload={true} showDelete={true} />
            )}

            {/* Onglet Mes Partages ZIP */}
            {activeTab === 'mes-partages' && (
              <ZipSharesDashboard mode="sent" />
            )}

            {/* Onglet Messagerie */}
            {activeTab === 'messagerie' && (
              <MessagerieDashboard />
            )}

            {activeTab === 'dommages' && (
              <div className="space-y-6">
                {/* Statistiques Dommages */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-l-4 border-l-orange-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-600">En Cours</p>
                          <p className="text-3xl font-bold text-orange-600">
                            {dommages.filter(s => s.statut === 'en_cours').length}
                          </p>
                        </div>
                        <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                          <AlertCircle className="h-6 w-6 text-orange-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-600">Résolus</p>
                          <p className="text-3xl font-bold text-green-600">
                            {dommages.filter(s => s.statut === 'resolu').length}
                          </p>
                        </div>
                        <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                          <Shield className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-600">Total</p>
                          <p className="text-3xl font-bold text-blue-600">
                            {dommages.length}
                          </p>
                        </div>
                        <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <FileText className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Liste des dommages */}
                <Card>
                  <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Mes Déclarations de Dommage
                    </CardTitle>
                    <CardDescription className="text-white/90">
                      Suivez l'état de vos déclarations
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {/* Bouton Déclarer un dommage toujours visible */}
                    <div className="flex justify-end mb-4">
                      <Button 
                        onClick={() => setShowDeclarationForm(true)}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Déclarer un dommage
                      </Button>
                    </div>
                    
                    {dommages.length === 0 ? (
                      <div className="text-center py-12">
                        <AlertCircle className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                        <p className="text-slate-600 font-medium">Aucun dommage déclaré</p>
                        <p className="text-sm text-slate-500 mt-2">
                          Vous n'avez pas encore de déclaration de dommage
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4">
                        {dommages.map((dommage) => (
                      <div key={dommage.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold">{dommage.intitule_chantier || `Dommage du ${new Date(dommage.created_at || dommage.date_dommage).toLocaleDateString('fr-FR')}`}</h3>
                            <p className="text-sm text-slate-600">{dommage.description}</p>
                            {dommage.adresse_travaux && (
                              <p className="text-xs text-slate-500 mt-1">📍 {dommage.adresse_travaux}</p>
                            )}
                          </div>
                          <Badge className={
                            dommage.statut === 'resolu' ? 'bg-green-700 text-white' :
                            dommage.statut === 'en_cours' ? 'bg-blue-700 text-white' :
                            dommage.statut === 'en_attente' ? 'bg-yellow-600 text-white' :
                            'bg-gray-600 text-white'
                          }>
                            {dommage.statut === 'en_attente' ? 'En attente' :
                             dommage.statut === 'en_cours' ? 'En cours' :
                             dommage.statut === 'resolu' ? 'Résolu' :
                             dommage.statut || 'Nouveau'}
                          </Badge>
                        </div>
                        {dommage.type_demande && (
                          <p className="text-sm text-slate-500">Type: {dommage.type_demande.replace('_', ' ')}</p>
                        )}
                        {dommage.evaluation_financiere && (
                          <p className="text-sm font-semibold">Estimation: {dommage.evaluation_financiere}€</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
              </div>
            )}

            {activeTab === 'pv-chantiers' && (
              <ProcesVerbaux />
            )}

            {activeTab === 'bibliotheque' && (
              <BibliothequeDocuments />
            )}

            {activeTab === 'formations' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <BookOpen className="h-6 w-6 text-purple-600" />
                  Mes Formations FORMA'SCOP
                </h2>
                <MesFormations />
              </div>
            )}

            {activeTab === 'wallet' && (
              <div>
                <iframe 
                  src="/wallet-management" 
                  style={{width: '100%', height: '100vh', border: 'none'}}
                  title="Wallet Management"
                />
              </div>
            )}

            {activeTab === 'abonnement' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Gestion des Abonnements</h2>
                <MemberSubscriptionsNew getAuthHeader={getAuthHeader} onRefresh={fetchMemberData} />
              </div>
            )}

            {activeTab === 'profil' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Mon Profil</h2>
                <MemberProfileEdit member={member} onUpdate={fetchMemberData} getAuthHeader={getAuthHeader} />
              </div>
            )}
          </DashboardContent>
        </div>
        
        {/* Legal Notice Footer */}
        <div className="mt-8 bg-slate-100 p-4 rounded-lg">
          <p className="text-xs text-slate-600 leading-relaxed">
            Les garanties solidaires relèvent exclusivement du champ de l'entraide associative. 
            Elle ne constitue ni une assurance, ni un convention d'indemnisation au sens du Code des assurances. 
            Elle est conforme : – à la jurisprudence du Conseil d'État, 10 février 1984 (Association d'entraide des transporteurs routiers) ; 
            – à la doctrine ACPR 2019 & 2022, reconnaissant les dispositifs de solidarité non contractuels ; 
            – et à la loi du 31 juillet 2014 relative à l'économie sociale et solidaire ; et la loi coopérative de 1947.
          </p>
        </div>
      </div>
      
      {/* Formulaire de déclaration de dommage */}
      <DeclarationDommageForm
        isOpen={showDeclarationForm}
        onClose={() => setShowDeclarationForm(false)}
        getAuthHeader={getAuthHeader}
        memberData={member}
        onSuccess={() => {
          fetchMemberData();
          setActiveTab('dommages');
        }}
      />
      
      {/* Tour guidé de bienvenue */}
      <WelcomeTour space="member" />
      
      {/* Centre d'aide amélioré */}
      <HelpCenterV2 space="member" />
    </div>
  );
}
